/**
 * ResidentEpisodePanel — Resident Trends v2 on the resident profile:
 * the resident's open episode (with full lifecycle actions), their episode
 * history with outcome badges, and any active expected-behavior mutes.
 *
 * Renders null while loading or when the resident has nothing to show.
 *
 * Props: campusId, residentId
 */

import { useState, useEffect, useCallback } from 'react';
import { getUserId, getSessionId } from '../../services/session.js';
import { fetchResidentEpisodes, updateResidentEpisode, clearTrendMute } from './episodesApi.js';
import EpisodeCard from './EpisodeCard.jsx';
import { STATUS_CONFIG, OUTCOME_CONFIG, FAMILY_LABELS } from './trendConfig.js';

const OPEN_STATUSES = ['new', 'acknowledged', 'in_progress'];

const formatDate = (value) => (value ? String(value).slice(0, 10) : '');

const HistoryRow = ({ episode }) => {
    const status = STATUS_CONFIG[episode.status] || {};
    const outcome = OUTCOME_CONFIG[episode.outcome] || null;
    return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', padding: '6px 10px', borderRadius: 'var(--radius-sm)', background: 'var(--color-bg-tertiary)', marginBottom: 4, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>
                {formatDate(episode.opened_date)} → {formatDate(episode.closed_date) || 'open'}
            </span>
            <span style={{ fontSize: 10, fontWeight: 600, color: status.color, textTransform: 'uppercase' }}>{status.label}</span>
            {episode.outcome && episode.outcome !== 'pending' && outcome ? (
                <span style={{ fontSize: 10, fontWeight: 600, color: outcome.color, background: `${outcome.color}14`, padding: '1px 8px', borderRadius: 'var(--radius-full)' }}>{outcome.label}</span>
            ) : null}
            <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)', flex: 1, minWidth: 120, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {episode.narrative || ''}
            </span>
        </div>
    );
};

const ResidentEpisodePanel = ({ campusId, residentId }) => {
    const [data, setData] = useState({ episodes: [], mutes: [] });
    const [loading, setLoading] = useState(true);
    const [processingIds, setProcessingIds] = useState(new Set());
    const [actionError, setActionError] = useState(null);

    const fetchData = useCallback(async () => {
        setLoading(true);
        const result = await fetchResidentEpisodes({ campusId, residentId, status: 'all', limit: 25 });
        setData({ episodes: result.episodes || [], mutes: result.mutes || [] });
        setLoading(false);
    }, [campusId, residentId]);

    useEffect(() => { fetchData(); }, [fetchData]);

    const applyUpdate = useCallback(async (episodeId, status, extra = {}) => {
        setProcessingIds((prev) => new Set(prev).add(episodeId));
        setActionError(null);
        const [ok, errMsg] = await updateResidentEpisode({
            campusId, episodeId, status, ...extra,
            userId: getUserId(), clientId: getSessionId(),
        });
        if (ok) {
            await fetchData();
        } else {
            setActionError(errMsg || 'Update failed. Please try again.');
        }
        setProcessingIds((prev) => { const n = new Set(prev); n.delete(episodeId); return n; });
    }, [campusId, fetchData]);

    const handleAcknowledge = useCallback((id) => applyUpdate(id, 'acknowledged'), [applyUpdate]);
    const handleResolve = useCallback((id, actionTaken) => applyUpdate(id, 'resolved', { actionTaken }), [applyUpdate]);
    const handleDismiss = useCallback((id, reason, muteDays) => applyUpdate(id, 'dismissed', { dismissReason: reason, muteDays }), [applyUpdate]);

    const handleUnmute = useCallback(async (threadId) => {
        setActionError(null);
        const [ok, errMsg] = await clearTrendMute({ campusId, threadId, userId: getUserId(), clientId: getSessionId() });
        if (ok) await fetchData();
        else setActionError(errMsg || 'Update failed. Please try again.');
    }, [campusId, fetchData]);

    const openEpisodes = data.episodes.filter((e) => OPEN_STATUSES.includes(e.status));
    const closedEpisodes = data.episodes.filter((e) => !OPEN_STATUSES.includes(e.status));

    if (loading || (data.episodes.length === 0 && data.mutes.length === 0)) return null;

    return (
        <div style={{ marginBottom: 'var(--space-3)' }}>
            {actionError ? (
                <div style={{ margin: '0 0 8px', padding: '6px 12px', borderRadius: 'var(--radius-md)', background: 'rgba(231,76,60,0.08)', color: 'var(--color-danger)', fontSize: 'var(--font-size-xs)' }}>
                    {actionError}
                </div>
            ) : null}

            {openEpisodes.map((episode) => (
                <EpisodeCard
                    key={episode.episode_id}
                    episode={episode}
                    showResident={false}
                    onAcknowledge={handleAcknowledge}
                    onResolve={handleResolve}
                    onDismiss={handleDismiss}
                    isProcessing={processingIds.has(episode.episode_id)}
                />
            ))}

            {data.mutes.length > 0 ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', margin: '4px 0 8px' }}>
                    {data.mutes.map((mute) => (
                        <span key={mute.thread_id} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 'var(--font-size-xs)', padding: '2px 10px', borderRadius: 'var(--radius-full)', background: 'var(--color-bg-tertiary)', color: 'var(--color-text-muted)' }}>
                            🔕 {FAMILY_LABELS[mute.signal_family] || mute.signal_family} trends muted until {formatDate(mute.muted_until)}
                            <span onClick={() => handleUnmute(mute.thread_id)} style={{ cursor: 'pointer', fontWeight: 600, color: 'var(--color-text-body)' }} title="Clear this mute">✕</span>
                        </span>
                    ))}
                </div>
            ) : null}

            {closedEpisodes.length > 0 ? (
                <div style={{ marginTop: 6 }}>
                    <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 4 }}>
                        Trend history
                    </div>
                    {closedEpisodes.map((episode) => <HistoryRow key={episode.episode_id} episode={episode} />)}
                </div>
            ) : null}
        </div>
    );
};

export default ResidentEpisodePanel;
